import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import * as XLSX from "xlsx";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import ReactApexChart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { productData } from "../../components/common/globalapi/Api";

// placeholder for missing icons
const Icons = {
    money: <i className="fa fa-wallet"></i>,
    expenses: <i className="fa fa-cash-register"></i>,
    net: <i className="fa fa-chart-line"></i>,
    game: <i className="fa fa-gamepad"></i>,
    customer: <i className="fa fa-users"></i>,
    edit: <i className="fa fa-pencil-alt"></i>,
    delete: <i className="fa fa-trash-alt"></i>,
};

// ApexChart Component
const ApexChart = ({ dashBoardDetails }) => {
    const totalRevenue = dashBoardDetails?.totalRevenue || 0;
    const totalGamesPlayed = dashBoardDetails?.totalGamesPlayed || 0;
    // Use unique customers count (totalCustomers), not order count (totalOrders)
    const totalCustomers = dashBoardDetails?.totalCustomers ?? dashBoardDetails?.totalOrders ?? 0;

    const series = [totalRevenue, totalGamesPlayed, totalCustomers];

    const options = {
        chart: {
            height: 250,
            type: "radialBar",
            background: 'transparent',
        },
        colors: ['#00FF88', '#FFB800', '#00D1FF'], // Vibrant colors
        plotOptions: {
            radialBar: {
                hollow: {
                    size: "30%",
                },
                track: {
                    background: "rgba(255, 255, 255, 0.1)",
                    strokeWidth: "100%",
                },
                dataLabels: {
                    name: {
                        fontSize: "18px",
                        color: "#FFFFFF",
                    },
                    value: {
                        fontSize: "14px",
                        color: "#FFFFFF",
                    },
                },
            },
        },
        labels: ["Revenue", "Orders", "Customers"],
        legend: {
            show: true,
            position: "bottom",
            labels: {
                colors: "#FFFFFF",
            },
            fontSize: '12px',
            formatter: (seriesName, opts) => {
                return `${seriesName}: ${opts.w.globals.series[opts.seriesIndex]}`;
            },
        },
    };

    return (
        <div className="text-center py-3">
            <ReactApexChart
                options={options}
                series={series}
                type="radialBar"
                height={250}
            />
        </div>
    );
};

const Reports = () => {
    const savedBranch = sessionStorage.getItem("selectedBranch");
    const role = sessionStorage.getItem("role");
    const userId = sessionStorage.getItem("userId");
    const apiUserId = userId || 11; // use session userId when available, otherwise fallback to 11 as per latest user example

    const {
        handleSubmit,
        control,
        setValue,
        watch,
    } = useForm({
        defaultValues: {
            selectBranch: savedBranch || "",
        },
    });

    const navigate = useNavigate();
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const handleOpenOrderModal = (order) => {
        setSelectedOrder(order);
    };
    const [selectedBranch, setSelectedBranch] = useState(savedBranch || "");
    const [searchTerm, setSearchTerm] = useState("");
    const [fromDate, setFromDate] = useState(moment().startOf('month').toDate());
    const [toDate, setToDate] = useState(moment().toDate());
    const [selectedCategory, setSelectedCategory] = useState("All");

    const storedBranchArray = JSON.parse(sessionStorage.getItem("branchNames")) || [];

    // Fetch product categories from backend (same as Fast Billing tab in NewSales)
    const companyDetails = (() => {
        try { return JSON.parse(localStorage.getItem("companyDetails") || "{}"); } catch { return {}; }
    })();
    const registerId = companyDetails?.id || apiUserId;

    const { data: inventoryDetails } = useQuery({
        queryKey: ["reportInventory", registerId],
        queryFn: () => productData(registerId, false),
        enabled: !!registerId,
        refetchOnWindowFocus: false,
    });
    const inventory = inventoryDetails?.data || [];
    // Build unique category list from product catalog
    const categories = ["All", ...new Set((Array.isArray(inventory) ? inventory : []).map(p => p?.category).filter(Boolean))];

    // Fetching dashboard data
    const { data: dashBoardDetails, isLoading: dashboardLoading } = useQuery({
        queryKey: ["dashboard", apiUserId, fromDate, toDate],
        queryFn: async () => {
            const formattedFromDate = moment(fromDate).format("YYYY-MM-DD");
            const formattedToDate = moment(toDate).format("YYYY-MM-DD");
            const response = await axios.get(
                `https://capi.2kvirtualworld.com/b2bfastbilling/api/dashboard/details/${apiUserId}?fromDate=${formattedFromDate}&toDate=${formattedToDate}`
            );
            // Map the new API response fields to match the UI's expected format
            const data = response.data || {};
            return {
                totalRevenue: data.totalSalesAmount || 0,
                totalExpenses: data.totalPurchaseAmount || 0,
                netRevenue: data.netProfit || 0,
                totalOrders: data.totalOrders || 0,
                totalGamesPlayed: data.totalOrders || 0,
                totalProducts: data.totalProducts || 0,
                totalCustomers: data.totalCustomers || 0,
                mostOrderedItems: data.mostOrderedProducts || [],
            };
        },
        enabled: !!apiUserId && !!fromDate && !!toDate,
    });

    // Prefer computed dashboard if backend dashboard is empty
    // (moved below after computedDashboard is declared)

    // Fetching orders
    const { data: ordersResponse, isLoading: ordersLoading, refetch: refetchOrders } = useQuery({
        queryKey: ["orders", selectedBranch],
        queryFn: async () => {
            const response = await axios.get(`${process.env.REACT_APP_HOST}/orders/${selectedBranch}`);
            return response.data;
        },
        enabled: !!selectedBranch,
    });

    // Also fetch the global orders as requested for Reports page
    const {
        data: allOrdersResponse,
        isLoading: allOrdersLoading,
        refetch: refetchAllOrders,
    } = useQuery({
        queryKey: ["allOrders", apiUserId],
        queryFn: async () => {
            const response = await axios.get(
                `https://capi.2kvirtualworld.com/b2bfastbilling/api/orders/getAll/${apiUserId}`
            );
            return response.data;
        },
        enabled: true,
        refetchOnWindowFocus: false,
    });

    // Prefer the explicit all-orders response for this Reports table when available
    const orderData = Array.isArray(allOrdersResponse) ? allOrdersResponse : (ordersResponse?.data || []);

    // Fetch purchases to compute expenses
    const {
        data: purchasesResponse,
        isLoading: purchasesLoading,
        refetch: refetchPurchases,
    } = useQuery({
        queryKey: ["purchases", apiUserId],
        queryFn: async () => {
            const resp = await axios.get(`${process.env.REACT_APP_HOST}/api/Purchases/getall/${apiUserId}`);
            return resp.data;
        },
        enabled: !!apiUserId,
        refetchOnWindowFocus: false,
    });

    const purchaseData = Array.isArray(purchasesResponse) ? purchasesResponse : (purchasesResponse?.data || []);

    // Compute dashboard metrics from sales (orderData) and purchases
    const computeOrderAmount = (order) => {
        // Flat Item response structure provided by user
        if (order?.highLevelCustomerPrice && order?.quantity) {
            const basePrice = Number(order.highLevelCustomerPrice) * Number(order.quantity);
            const gstAmount = (basePrice * Number(order.gst || 0)) / 100;
            return basePrice + gstAmount;
        }
        return Number(order?.receivedAmount ?? order?.totalAmount ?? order?.total ?? 0) || 0;
    };

    const totalRevenue = (orderData || []).reduce((sum, o) => sum + computeOrderAmount(o), 0);
    const totalOrdersCount = (orderData || []).length;
    // Unique customers by name (safe dedup)
    const totalCustomersCount = new Set(
        (orderData || []).map(o => (o?.customerName || '').toString().trim()).filter(Boolean)
    ).size;

    const computePurchaseTotal = (p) => {
        // Flat Item response structure provided by user
        if (p?.purchasePrice && p?.quantity) {
            const basePrice = Number(p.purchasePrice) * Number(p.quantity);
            const gstAmount = (basePrice * Number(p.gst || 0)) / 100;
            return basePrice + gstAmount;
        }

        // Legacy p may be a flat record or contain vendorDetails/items
        const list = p?.vendorDetails || p?.items || (p?.productName || p?.product ? [p] : []);
        if (Array.isArray(list) && list.length > 0) {
            return list.reduce((s, it) => s + (Number(it?.purchasePrice ?? it?.unitPrice ?? it?.price ?? 0) * Number(it?.quantity ?? 1) || 0), 0);
        }
        return Number(p?.totalAmount ?? p?.total ?? 0) || 0;
    };

    const totalExpenses = (purchaseData || []).reduce((sum, p) => sum + computePurchaseTotal(p), 0);
    const netRevenue = totalRevenue - totalExpenses;

    const computedDashboard = {
        totalRevenue: totalRevenue || 0,
        totalExpenses: totalExpenses || 0,
        netRevenue: netRevenue || 0,
        totalOrders: totalOrdersCount || 0,
        // Total Games Played = Total Orders placed (billing context)
        totalGamesPlayed: totalOrdersCount || 0,
        // Unique customers count
        totalCustomers: totalCustomersCount || 0,
    };

    // Prefer computed dashboard if backend dashboard is empty or missing keys
    const displayDashboard = (dashBoardDetails && dashBoardDetails.totalOrders !== undefined) ? dashBoardDetails : computedDashboard;
    
    // Merge local computations for missing fields (like totalCustomers) into displayDashboard if it came from API
    if (dashBoardDetails && dashBoardDetails.totalOrders !== undefined && dashBoardDetails.totalCustomers === undefined) {
        displayDashboard.totalCustomers = computedDashboard.totalCustomers;
    }

    // Build a set of product names belonging to the selected category for fast lookup
    const categoryProductNames = selectedCategory === "All"
        ? null
        : new Set(
            (Array.isArray(inventory) ? inventory : [])
                .filter(p => p?.category === selectedCategory)
                .map(p => (p?.productName || p?.productNameEnglish || '').toLowerCase())
                .filter(Boolean)
        );

    const filteredData = (orderData || [])
        .filter((order) => {
            const search = searchTerm.trim().toLowerCase();
            const dateStr = order?.salesDate || order?.orderDate || order?.createdAt;
            const orderDate = moment(dateStr);
            const isWithinDateRange = !dateStr || orderDate.isBetween(
                moment(fromDate).startOf('day'), moment(toDate).endOf('day'), null, '[]'
            );

            // Search across customerName, productName/itemName, id
            const matchesSearch = search === "" ||
                (order?.customerName || "").toString().toLowerCase().includes(search) ||
                (order?.items?.map(i => i?.itemName || i?.productName).join(' ') || order?.productName || "").toString().toLowerCase().includes(search) ||
                (order?.id || order?.orderId || "").toString().includes(search);

            // Filter by selected category — check if any item in the order belongs to the category
            const matchesCategory = !categoryProductNames ||
                (order?.items || []).some(i =>
                    categoryProductNames.has((i?.itemName || i?.productName || '').toLowerCase())
                ) ||
                categoryProductNames.has((order?.productName || '').toLowerCase());

            return isWithinDateRange && matchesSearch && matchesCategory;
        })
        .sort((a, b) => moment(b.salesDate || b.orderDate || b.createdAt).diff(moment(a.salesDate || a.orderDate || a.createdAt)));

    const downloadExcel = () => {
        if (!filteredData || filteredData.length === 0) {
            toast.info("No data to download for the selected range/category.");
            return;
        }
        const formattedData = filteredData.map((item, index) => {
            const dateVal = item?.salesDate || item?.orderDate || item?.createdAt;
            const itemNames = item?.items?.map(d => d?.itemName || d?.productName).filter(Boolean).join(", ") || item?.productName || "";
            const quantities = item?.items?.map(d => d?.quantity).filter(v => v != null).join(", ") || (item?.quantity || "");
            const total = item?.totalAmount ?? item?.total ??
                (item?.items ? item.items.reduce((sum, i) => sum + (Number(i.total) || 0), 0) : 0);
            const paymentMode = item?.transactionMode || item?.paymentMode || "Hand Cash";
            const txnStatus = item?.transactionStatus || "";
            const category = item?.items?.[0]?.category ||
                (Array.isArray(inventory) ? inventory.find(p =>
                    (p?.productName || p?.productNameEnglish || '').toLowerCase() ===
                    (item?.items?.[0]?.itemName || item?.productName || '').toLowerCase()
                )?.category : '') || "";
            return {
                "S.NO": index + 1,
                "ORDER ID": item?.orderidincrement || item?.orderId || item?.id || "",
                "ORDER DATE": dateVal ? moment(dateVal).format("DD-MM-YYYY") : "",
                "CUSTOMER NAME": item?.customerName || "",
                "ITEMS": itemNames,
                "QUANTITY": quantities,
                "CATEGORY": category,
                "PAYMENT MODE": paymentMode,
                "TRANSACTION STATUS": txnStatus,
                "TOTAL (₹)": Number(total) || 0,
            };
        });

        const ws = XLSX.utils.json_to_sheet(formattedData);
        // Auto column widths
        const colWidths = Object.keys(formattedData[0] || {}).map(k => ({
            wch: Math.max(k.length, 15)
        }));
        ws['!cols'] = colWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Orders");
        const filename = `Orders_${selectedCategory !== 'All' ? selectedCategory + '_' : ''}${moment(fromDate).format('DDMMYYYY')}_${moment(toDate).format('DDMMYYYY')}.xlsx`;
        XLSX.writeFile(wb, filename);
        toast.success("Excel downloaded successfully!");
    };

    const handleCheckboxChange = (id) => {
        setSelectedRows(prev => prev.includes(id) ? prev.filter(rid => rid !== id) : [...prev, id]);
    };

    const deleteMutation = useMutation({
        mutationFn: async (ids) => {
            return await Promise.all(ids.map(id => axios.delete(`${process.env.REACT_APP_HOST}/orders/${selectedBranch}/${id}`)));
        },
        onSuccess: () => {
            toast.success("Deleted successfully");
            setSelectedRows([]);
            refetchOrders();
        },
        onError: () => toast.error("Error deleting records"),
    });

    const handleDelete = () => {
        if (window.confirm("Are you sure you want to delete selected records?")) {
            deleteMutation.mutate(selectedRows);
        }
    };

    return (
        <div className="dashboard-wrapper p-3">
            <div className="row g-4">
                {/* Main Content (80%) */}
                <div className="col-lg-10">
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                        <h4 className="fw-bold mb-0">DASHBOARD</h4>
                        <div className="d-flex gap-2 align-items-center flex-wrap">
                            <div className="d-flex align-items-center bg-white border rounded px-2">
                                <DatePicker
                                    selected={fromDate}
                                    onChange={(date) => setFromDate(date || moment().startOf('month').toDate())}
                                    className="border-0 bg-transparent py-1"
                                    dateFormat="MMM d, yyyy"
                                    maxDate={toDate}
                                />
                                <span className="px-2 text-muted">to</span>
                                <DatePicker
                                    selected={toDate}
                                    onChange={(date) => setToDate(date || moment().toDate())}
                                    className="border-0 bg-transparent py-1"
                                    dateFormat="MMM d, yyyy"
                                    minDate={fromDate}
                                />
                            </div>
                            {/* Category Filter Dropdown */}
                            <select
                                className="form-select"
                                style={{ width: '160px' }}
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                title="Filter by Product Category"
                            >
                                {categories.map((cat, idx) => (
                                    <option key={idx} value={cat}>{cat === "All" ? "All Categories" : cat}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                className="form-control"
                                style={{ width: '200px' }}
                                placeholder="Search orders..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <button className="btn btn-success" onClick={downloadExcel} title="Download filtered orders as Excel">
                                <i className="fa fa-download me-2"></i>Download
                            </button>
                        </div>
                    </div>

                    {/* Metric Cards */}
                    <div className="row g-3 mb-4">
                        <div className="col">
                            <div className="card border-0 shadow-sm metric-card income h-100">
                                <div className="card-body">
                                    <h3 className="fw-bold mt-3 mb-1">₹ {displayDashboard?.totalRevenue || 0}</h3>
                                    <small className="opacity-75">Total Sales</small>
                                </div>
                            </div>
                        </div>

                        <div className="col">
                            <div className="card border-0 shadow-sm metric-card expenses h-100">
                                <div className="card-body">
                                    <h3 className="fw-bold mt-3 mb-1">₹ {displayDashboard?.totalExpenses || 0}</h3>
                                    <small className="opacity-75">Total Purchase</small>
                                </div>
                            </div>
                        </div>

                        <div className="col">
                            <div className="card border-0 shadow-sm metric-card net h-100">
                                <div className="card-body">
                                    <h3 className="fw-bold mt-3 mb-1">₹ {displayDashboard?.netRevenue || 0}</h3>
                                    <small className="opacity-75">Net Revenue</small>
                                </div>
                            </div>
                        </div>

                        <div className="col">
                            <div className="card border-0 shadow-sm metric-card games h-100">
                                <div className="card-body">
                                    <h3 className="fw-bold mt-3 mb-1">{displayDashboard?.totalGamesPlayed ?? computedDashboard.totalGamesPlayed ?? 0}</h3>
                                    <small className="opacity-75">Total Orders</small>
                                </div>
                            </div>
                        </div>

                        <div className="col">
                            <div className="card border-0 shadow-sm metric-card customers h-100">
                                <div className="card-body">
                                    <h3 className="fw-bold mt-3 mb-1">{displayDashboard?.totalCustomers ?? computedDashboard.totalCustomers ?? 0}</h3>
                                    <small className="opacity-75">Total Customers</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table Container */}
                    <div className="card border-0 shadow-sm p-0 overflow-hidden">
                        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 fw-bold">Recent Orders</h6>
                            {selectedRows.length > 0 && (
                                <button className="btn btn-sm btn-danger" onClick={handleDelete}>
                                    Delete Selected ({selectedRows.length})
                                </button>
                            )}
                        </div>
                        <div className="table-responsive" style={{ height: "40vh" }}>
                            <table className="table table-hover align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        {role === "superAdmin" && <th className="ps-4">Action</th>}
                                        <th>Order Date</th>
                                        <th>Items</th>
                                        <th>Amount</th>
                                        <th>Payment Mode</th>
                                        <th>Order ID</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(ordersLoading || allOrdersLoading) ? (
                                        <tr><td colSpan="6" className="text-center py-5"><div className="spinner-border spinner-border-sm me-2"></div>Loading orders...</td></tr>
                                    ) : filteredData?.length > 0 ? (
                                        filteredData.map((order, idx) => {
                                            const dateVal = order?.salesDate || order?.orderDate || order?.createdAt;
                                            const formattedDate = dateVal ? moment(dateVal).format("MMM D, yyyy") : '-';
                                            const itemsCount = order?.items ? order.items.length : (order?.quantity ? 1 : 0);
                                            const itemsLabel = order?.items ? `${itemsCount} items` : `${order?.quantity || 0} ${order?.quantityType || ''} ${order?.productName || order?.itemName || ''}`;

                                            // Calculate Row Amount handling flat API structure
                                            let amount = 0;
                                            if (order?.highLevelCustomerPrice && order?.quantity) {
                                                const basePrice = Number(order.highLevelCustomerPrice) * Number(order.quantity);
                                                const gstAmount = (basePrice * Number(order.gst || 0)) / 100;
                                                amount = basePrice + gstAmount;
                                            } else {
                                                amount = order?.totalAmount ?? order?.total ?? (order?.items ? order.items.reduce((s, i) => s + (Number(i.total) || 0), 0) : 0);
                                            }

                                            const paymentMode = order?.transactionMode || order?.paymentMode || 'Hand Cash';
                                            const orderIdLabel = order?.orderId || order?.orderidincrement || order?.id;

                                            return (
                                                <tr
                                                    key={idx}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleOpenOrderModal(order)}
                                                    data-bs-toggle="modal"
                                                    data-bs-target="#orderDetailsModal"
                                                >
                                                    {role === "superAdmin" && (
                                                        <td className="ps-4" onClick={(e) => e.stopPropagation()}>
                                                            <div className="d-flex gap-2">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    checked={selectedRows.includes(order.id)}
                                                                    onChange={() => handleCheckboxChange(order.id)}
                                                                />
                                                            </div>
                                                        </td>
                                                    )}
                                                    <td>{formattedDate}</td>
                                                    <td><span className="badge bg-light text-dark border">{itemsLabel}</span></td>
                                                    <td className="fw-bold text-primary">₹ {amount}</td>
                                                    <td>
                                                        <span className={`badge ${paymentMode === 'Online' ? 'bg-info-light text-info' : 'bg-success-light text-success'}`}>
                                                            {paymentMode}
                                                        </span>
                                                    </td>
                                                    <td className="text-muted">#{orderIdLabel}</td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr><td colSpan="6" className="text-center py-5 text-muted">No records found for the selected range.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Sidebar Reports (20%) */}
                <div className="col-lg-2">
                    <div className="sidebar-reports h-100 rounded p-3 bg-dark text-white shadow">
                        <h6 className="text-uppercase small fw-bold opacity-75 mb-4">Live Reports</h6>

                        <div className="report-section mb-5">
                            <h6 className="mb-3">Performance</h6>
                            <div className="apex-chart-container">
                                <ApexChart dashBoardDetails={displayDashboard} />
                            </div>
                        </div>

                        <div className="report-section">
                            <h6 className="mb-3">Most Ordered Items</h6>
                            <div className="list-group list-group-flush bg-transparent">
                                {/* Safe fallback: use mostOrderedItems from backend OR compute from local orderData */}
                                {(dashBoardDetails?.mostOrderedItems?.length
                                    ? dashBoardDetails.mostOrderedItems
                                    : (() => {
                                        // Compute top items locally from orderData
                                        const countMap = {};
                                        (orderData || []).forEach(order => {
                                            (order?.items || []).forEach(it => {
                                                const name = it?.itemName || it?.productName || '';
                                                if (name) countMap[name] = (countMap[name] || 0) + Number(it?.quantity || 1);
                                            });
                                            if (!order?.items && (order?.productName || order?.itemName)) {
                                                const name = order.productName || order.itemName;
                                                countMap[name] = (countMap[name] || 0) + Number(order?.quantity || 1);
                                            }
                                        });
                                        return Object.entries(countMap)
                                            .sort((a, b) => b[1] - a[1])
                                            .slice(0, 5)
                                            .map(([itemName, count]) => ({ itemName, count }));
                                    })()
                                ).map((item, idx) => {
                                    const name = item?.itemName || item?.productName || '-';
                                    return (
                                        <div key={idx} className="list-group-item bg-transparent border-0 px-0 py-1 d-flex align-items-center justify-content-between gap-1">
                                            <div className="d-flex align-items-center gap-1 flex-grow-1" style={{ minWidth: 0 }}>
                                                <span className="text-muted" style={{ fontSize: '10px', flexShrink: 0 }}>#{idx + 1}</span>
                                                <span
                                                    className="text-white"
                                                    style={{
                                                        fontSize: '11px',
                                                        overflow: 'hidden',
                                                        whiteSpace: 'nowrap',
                                                        textOverflow: 'ellipsis',
                                                        maxWidth: '100%',
                                                        display: 'block'
                                                    }}
                                                    title={name}
                                                >
                                                    {name}
                                                </span>
                                            </div>
                                            <span
                                                className="badge rounded-pill text-dark fw-bold"
                                                style={{ background: '#FFB800', flexShrink: 0, fontSize: '11px' }}
                                            >
                                                {item?.totalQuantity ?? item?.count ?? 0}
                                            </span>
                                        </div>
                                    );
                                })}
                                {((!dashBoardDetails?.mostOrderedItems?.length) && (orderData || []).length === 0) && (
                                    <p className="text-muted small text-center mt-2">No order data yet.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bootstrap 5 Modal for Order Details */}
                <div
                    className="modal fade"
                    id="orderDetailsModal"
                    tabIndex={-1}
                    aria-hidden="true"
                    data-bs-backdrop="static"
                >
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header bg-light pb-2">
                                <h5 className="modal-title fw-bold">Order Details</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                ></button>
                            </div>
                            <div className="modal-body p-4">
                                {selectedOrder ? (
                                    <div>
                                        <div className="row mb-4">
                                            <div className="col-md-6 mb-2">
                                                <p className="mb-1 text-muted small">Order ID</p>
                                                <p className="fw-bold fs-5 mb-0">#{selectedOrder?.orderId || selectedOrder?.orderidincrement || selectedOrder?.id || 'N/A'}</p>
                                            </div>
                                            <div className="col-md-6 mb-2 text-md-end">
                                                <p className="mb-1 text-muted small">Transaction Mode</p>
                                                <span className={`badge ${selectedOrder?.transactionMode === 'Online' ? 'bg-info-light text-info' : 'bg-success-light text-success'}`}>
                                                    {selectedOrder?.transactionMode || selectedOrder?.paymentMode || 'Hand Cash'}
                                                </span>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <p className="mb-1 text-muted small">Status</p>
                                                <span className={`badge ${selectedOrder?.transactionStatus === 'Completed' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                    {selectedOrder?.transactionStatus || 'Pending'}
                                                </span>
                                            </div>
                                            <div className="col-md-6 mb-2 text-md-end">
                                                <p className="mb-1 text-muted small">Invoice No</p>
                                                <p className="fw-medium mb-0">{selectedOrder?.invoiceNo || '-'}</p>
                                            </div>
                                        </div>

                                        <hr className="my-3 opacity-25" />

                                        <h6 className="fw-bold mb-3 text-secondary">Products</h6>

                                        <div className="table-responsive border rounded">
                                            <table className="table table-sm table-striped table-hover mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th className="py-2 px-3">Product Name</th>
                                                        <th className="py-2 px-3 text-center">Quantity</th>
                                                        <th className="py-2 px-3 text-end">Price</th>
                                                        <th className="py-2 px-3 text-end">Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {((selectedOrder?.items?.length ? selectedOrder.items : undefined) || (selectedOrder?.products?.length ? selectedOrder.products : undefined) || []).map((product, index) => {
                                                        const qty = Number(product?.quantity || 0);
                                                        const unitPrice = Number(product?.unitPrice || product?.highLevelCustomerPrice || product?.purchasePrice || product?.price || 0);
                                                        // Priority: Use pre-calculated price if available to avoid quantity double-multiplication
                                                        const lineTotal = product?.price ? Number(product?.price || 0) : (qty * unitPrice);
                                                        return (
                                                            <tr key={index}>
                                                                <td className="px-3 align-middle">{product?.productName || product?.itemName || product?.product || '-'}</td>
                                                                <td className="px-3 text-center align-middle">{qty} {product?.quantityType || ''}</td>
                                                                <td className="px-3 text-end align-middle">₹ {unitPrice.toFixed(2)}</td>
                                                                <td className="px-3 text-end align-middle">₹ {lineTotal.toFixed(2)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                    {!(selectedOrder?.items?.length) && !(selectedOrder?.products?.length) && (selectedOrder?.productName || selectedOrder?.itemName) && (() => {
                                                        const qty = Number(selectedOrder?.quantity || 0);
                                                        const unitPrice = Number(selectedOrder?.unitPrice || selectedOrder?.highLevelCustomerPrice || selectedOrder?.purchasePrice || selectedOrder?.price || 0);
                                                        const lineTotal = selectedOrder?.price ? Number(selectedOrder?.price || 0) : (qty * unitPrice);
                                                        return (
                                                            <tr>
                                                                <td className="px-3 align-middle">{selectedOrder?.productName || selectedOrder?.itemName || '-'}</td>
                                                                <td className="px-3 text-center align-middle">{qty} {selectedOrder?.quantityType || ''}</td>
                                                                <td className="px-3 text-end align-middle">₹ {unitPrice.toFixed(2)}</td>
                                                                <td className="px-3 text-end align-middle">₹ {lineTotal.toFixed(2)}</td>
                                                            </tr>
                                                        );
                                                    })()}
                                                    {!(selectedOrder?.items?.length) && !(selectedOrder?.products?.length) && !(selectedOrder?.productName) && !(selectedOrder?.itemName) && (
                                                        <tr>
                                                            <td colSpan="4" className="text-center py-3 text-muted">No products found.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                                <tfoot className="table-light fw-bold">
                                                    {(() => {
                                                        let subVal = 0;
                                                        const items = (selectedOrder?.items?.length ? selectedOrder.items : undefined) || (selectedOrder?.products?.length ? selectedOrder.products : undefined) || [];
                                                        if (items.length > 0) {
                                                            items.forEach(p => {
                                                                const qty = Number(p?.quantity || 0);
                                                                const unitPrice = Number(p?.unitPrice || p?.highLevelCustomerPrice || p?.purchasePrice || p?.price || 0);
                                                                subVal += (qty * unitPrice);
                                                            });
                                                        } else if (selectedOrder?.productName || selectedOrder?.itemName) {
                                                            const qty = Number(selectedOrder?.quantity || 0);
                                                            const unitPrice = Number(selectedOrder?.unitPrice || selectedOrder?.highLevelCustomerPrice || selectedOrder?.purchasePrice || selectedOrder?.price || 0);
                                                            subVal += (qty * unitPrice);
                                                        }

                                                        const totalAmt = Number(selectedOrder?.totalAmount ?? selectedOrder?.total ?? 0);
                                                        // Fallback discount calculation if explicit fields are missing/empty
                                                        const explicitDiscount = Number(selectedOrder?.discount || selectedOrder?.discountPrice || 0);
                                                        const calculatedDiscount = subVal > totalAmt ? (subVal - totalAmt) : 0;
                                                        const finalDiscount = explicitDiscount > 0 ? explicitDiscount : calculatedDiscount;

                                                        return (
                                                            <>
                                                                <tr>
                                                                    <td colSpan="3" className="text-end px-3">Subtotal</td>
                                                                    <td className="px-3 text-end">₹ {subVal.toFixed(2)}</td>
                                                                </tr>
                                                                {finalDiscount > 0 && (
                                                                    <tr>
                                                                        <td colSpan="3" className="text-end px-3">Discount</td>
                                                                        <td className="px-3 text-end text-danger">- ₹ {finalDiscount.toFixed(2)}</td>
                                                                    </tr>
                                                                )}
                                                                <tr>
                                                                    <td colSpan="3" className="text-end px-3 align-middle">Total Amount</td>
                                                                    <td className="px-3 text-end text-success fs-6 align-middle">
                                                                        ₹ {totalAmt.toFixed(2)}
                                                                    </td>
                                                                </tr>
                                                            </>
                                                        );
                                                    })()}
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-5 text-muted">
                                        <i className="fa fa-info-circle fs-1 mb-3 opacity-25"></i>
                                        <p className="mb-0">Order details unavailable</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Reports;
